import * as React from "react";
import styled from "styled-components";
import { useRecoilValue, useSetRecoilState } from "recoil";
import classNames from "classnames";
import { add } from "date-fns";
import { Button, Colors } from "@blueprintjs/core";
import {
  calendarAtoms,
  calendarSelectors,
  useCalendarContext,
} from "../CalendarContext";

export interface TimeSliceProps {
  className: string;
  style?: React.CSSProperties;
  startDate: Date;
}

const Container = styled.div`
  //&:hover {
  //  background-color: red;
  //}
  cursor: pointer;
  &.hovered {
    background-color: ${Colors.DARK_GRAY5};
  }
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

export const TimeSlice: React.FunctionComponent<TimeSliceProps> = ({
  className,
  style,
  startDate,
}) => {
  const isSelected = useRecoilValue(
    calendarSelectors.isSliceSelected(startDate)
  );
  const setSelectedSlice = useSetRecoilState(calendarAtoms.hoveredSlice);
  const { daySliceInMinutes, onCreateSelection } = useCalendarContext();

  const endBounds = React.useMemo(() => {
    return add(startDate, { minutes: daySliceInMinutes });
  }, [daySliceInMinutes, startDate]);

  const onMouseDown = React.useCallback<
    React.MouseEventHandler<HTMLDivElement>
  >(
    (e) => {
      e.preventDefault();
      setSelectedSlice({
        bound1: startDate,
        bound2: endBounds,
      });
    },
    [endBounds, setSelectedSlice, startDate]
  );

  const onMouseEntered = React.useCallback<
    React.MouseEventHandler<HTMLDivElement>
  >(
    (e) => {
      if (e.buttons === 1) {
        setSelectedSlice((prev) => {
          if (prev.bound2?.toISOString() === endBounds?.toISOString()) {
            return prev;
          }
          if (prev.bound1.toISOString() === endBounds.toISOString()) {
            return {
              bound1: startDate,
              bound2: prev.bound2,
            };
          }
          return {
            bound1: prev.bound1,
            bound2: endBounds,
          };
        });
      }
    },
    [endBounds, setSelectedSlice, startDate]
  );

  const [showCreateButton, setShowCreateButton] = React.useState(false);

  const onMouseUp = React.useCallback<
    React.MouseEventHandler<HTMLDivElement>
  >(() => {
    if (isSelected) {
      setShowCreateButton(true);
    }
  }, [isSelected]);

  React.useEffect(() => {
    if (!isSelected) {
      setShowCreateButton(false);
    }
  }, [isSelected]);

  const onAddClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      onCreateSelection();
    },
    [onCreateSelection]
  );

  return (
    <Container
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseEnter={onMouseEntered}
      data-slice={startDate.toISOString()}
      className={classNames(className, { hovered: isSelected })}
      style={style}
    >
      {showCreateButton ? (
        <Button
          intent={"danger"}
          minimal
          small
          icon={"small-plus"}
          onMouseDown={onAddClick}
        />
      ) : null}
    </Container>
  );
};
